import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_UMS_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please sign in again to continue.';

type UnauthorizedHandler = (reason: string) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;
let unauthorizedHandlerFiring = false;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null) {
  unauthorizedHandler = handler;
}

export function triggerSessionExpired(reason: string = SESSION_EXPIRED_MESSAGE) {
  if (!unauthorizedHandler || unauthorizedHandlerFiring) return;
  unauthorizedHandlerFiring = true;
  try {
    unauthorizedHandler(reason);
  } finally {
    // Reset on next tick so multiple in-flight 401s don't each fire a logout,
    // but a future genuinely-new auth failure still can.
    setTimeout(() => { unauthorizedHandlerFiring = false; }, 0);
  }
}

/**
 * Helper to get the token directly if needed for specialized calls,
 * though the interceptor below handles this automatically for 99% of cases.
 */
export async function getRawAuthToken() {
  const session = await fetchAuthSession({ forceRefresh: true });
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("No idToken available");
  return token;
}

// REQUEST INTERCEPTOR: Automatically injects the JWT before every call
apiClient.interceptors.request.use(async (config) => {
  try {
    // Attempt to get the token
    const token = await getRawAuthToken();

    if (token) {
      config.headers.Authorization = token;
    }
  } catch (error) {
    // DO NOT REJECT. Just log it and let the request proceed.
    // If the endpoint is public (Auth: NONE), it will succeed.
    // If the endpoint is private, the server will return a 401 anyway.
    console.warn("Guest session: Proceeding without Authorization header.");
  }

  return config; // Always return config to let the request happen
});


// RESPONSE INTERCEPTOR: on 401, force-refresh the Cognito session once and retry.
// If the retry still 401s (or the refresh itself fails), trigger the global
// session-expired handler so the user is logged out with a clear message
// rather than being left in a half-authenticated state.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retriedAfterAuthRefresh) {
      original._retriedAfterAuthRefresh = true;
      try {
        const token = await getRawAuthToken();
        original.headers = original.headers || {};
        original.headers.Authorization = token;
        return apiClient(original);
      } catch (refreshError) {
        console.warn("Token refresh after 401 failed; logging user out.", refreshError);
        triggerSessionExpired();
        return Promise.reject(error);
      }
    }

    // Retry already happened and we're still unauthorized — session is dead.
    if (error.response?.status === 401 && original?._retriedAfterAuthRefresh) {
      triggerSessionExpired();
    }

    return Promise.reject(error);
  }
);

export default apiClient;